import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { addDays, format, isSameDay, startOfDay } from 'date-fns';
import { es } from 'date-fns/locale';
import { ArrowLeft, ArrowRight, Check, ChevronLeft, ChevronRight, Download, Info, Sun, Sunset } from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { FormField, Specialty } from '../types';
import { V2, SpecialtyIcon, initials, contactLink, usePageTitle } from './shared';

interface Pro { id: number; name: string; specialty: string; bio?: string }
const STEPS = ['Horario', 'Tus datos', 'Confirmar'];
const ANY = 0;

interface SlotRow { date: string; time: string; professionalIds: number[] }
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const BookingV2: React.FC = () => {
  usePageTitle('Pedir turno');
  const { specialtyId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();

  const [spec, setSpec] = useState<Specialty | null>(null);
  const [pros, setPros] = useState<Pro[]>([]);
  const [slotRows, setSlotRows] = useState<SlotRow[] | null>(null);
  const [loadErr, setLoadErr] = useState(false);

  const [step, setStep] = useState(0);
  const [proId, setProId] = useState<number>(ANY);
  const today = useMemo(() => startOfDay(new Date()), []);
  const [weekStart, setWeekStart] = useState(today);
  const [date, setDate] = useState<Date | null>(null);
  const [time, setTime] = useState('');
  const [form, setForm] = useState<Record<string, any>>({ name: '', email: '', phone: '' });
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [submitErr, setSubmitErr] = useState('');
  const [assignedPro, setAssignedPro] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [slotTakenMsg, setSlotTakenMsg] = useState('');
  // No published slots: patients can still send a request and the clinic coordinates the time.
  const [requestMode, setRequestMode] = useState(false);

  useEffect(() => {
    if (!specialtyId) return;
    Promise.all([
      api.get(`/public/specialties/${specialtyId}`),
      api.get('/public/professionals'),
    ]).then(([s, p]) => {
      setSpec(s.data);
      setPros((p.data as Pro[]).filter(x => x.specialty === s.data.name));
    }).catch(() => setLoadErr(true));
  }, [specialtyId]);

  // Real availability: fetched for the next ~month (API caps a request at 31 days), refetched when the professional changes.
  const loadSlots = React.useCallback(async () => {
    if (!specialtyId) return;
    setSlotRows(null);
    try {
      const from = format(new Date(), 'yyyy-MM-dd');
      const to = format(addDays(new Date(), 30), 'yyyy-MM-dd');
      const r = await api.get(`/public/specialties/${specialtyId}/slots`, { params: { from, to, professionalId: proId || undefined } });
      setSlotRows(r.data.slots);
    } catch { setSlotRows([]); setLoadErr(true); }
  }, [specialtyId, proId]);
  useEffect(() => { loadSlots(); }, [loadSlots]);

  // jump the week strip to the first day that actually has availability
  const firstDay = slotRows && slotRows[0] ? slotRows[0].date : null;
  useEffect(() => { if (firstDay && !date) setWeekStart(startOfDay(new Date(firstDay + 'T12:00:00'))); }, [firstDay]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (user) setForm(f => ({ ...f, name: f.name || user.name, email: f.email || user.email, phone: f.phone || (user as any).phone || '' }));
  }, [user]);

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, [step, sent]);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const daysWithSlots = useMemo(() => new Set((slotRows || []).map(r => r.date)), [slotRows]);
  const isOpen = (d: Date) => daysWithSlots.has(format(d, 'yyyy-MM-dd'));
  const slots = date ? (slotRows || []).filter(r => r.date === format(date, 'yyyy-MM-dd')).map(r => r.time) : [];
  const morning = slots.filter(s => +s.slice(0, 2) < 13);
  const afternoon = slots.filter(s => +s.slice(0, 2) >= 13);
  const proName = proId === ANY ? 'Primer profesional disponible' : pros.find(p => p.id === proId)?.name || '';
  const when = requestMode ? 'Día y horario a coordinar' : date && time ? `${cap(format(date, "EEEE d 'de' MMMM", { locale: es }))} · ${time} hs` : '';

  const setF = (k: string, v: any) => { setForm(f => ({ ...f, [k]: v })); setErrs(e => ({ ...e, [k]: '' })); };

  const validateDetails = () => {
    const e: Record<string, string> = {};
    if (form.name.trim().length < 2) e.name = 'Ingresá tu nombre completo';
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = 'Ingresá un email válido';
    if (form.phone && !/^[\d\s+()-]{8,20}$/.test(form.phone)) e.phone = 'Revisá el teléfono (solo números)';
    spec?.form_config.forEach(f => { if (f.required && !String(form[f.id] ?? '').trim()) e[f.id] = 'Este dato es necesario'; });
    setErrs(e);
    if (Object.keys(e).length) {
      // bring the first error into view (important on phones)
      setTimeout(() => document.querySelector('.err')?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 50);
    }
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (step === 0 && date && time) setStep(1);
    else if (step === 1 && validateDetails()) setStep(2);
  };

  const submit = async () => {
    if (!spec || (!date && !requestMode)) return;
    setBusy(true); setSubmitErr('');
    try {
      const formData: Record<string, any> = {};
      spec.form_config.forEach(f => { if (form[f.id] !== undefined) formData[f.id] = form[f.id]; });
      if (requestMode) {
        await api.post(`/public/specialties/${spec.id}/pre-appointment`, {
          patient_name: form.name.trim(), patient_email: form.email.trim(), patient_phone: form.phone || undefined, form_data: formData,
        });
        setSent(true);
        return;
      }
      const r = await api.post(`/public/specialties/${spec.id}/reserve`, {
        date: format(date!, 'yyyy-MM-dd'), time, professional_id: proId || undefined,
        patient_name: form.name.trim(), patient_email: form.email.trim(), patient_phone: form.phone || undefined, form_data: formData,
      });
      setAssignedPro(r.data.professionalName || '');
      setAccessToken(r.data.accessToken || '');
      setSent(true);
    } catch (err: any) {
      if (err.response?.status === 409) {
        // someone else took it while the patient was filling the form: go back and refresh availability
        setTime(''); setStep(0); setSlotTakenMsg('Ese horario se acaba de ocupar. Elegí otro, es rápido.');
        loadSlots();
      } else setSubmitErr(err.response?.data?.error || 'No pudimos enviar tu solicitud. Probá de nuevo en unos minutos.');
    } finally { setBusy(false); }
  };

  const downloadIcs = () => {
    if (!date) return;
    const [h, m] = time.split(':').map(Number);
    const s = new Date(date); s.setHours(h, m, 0, 0);
    const e = new Date(s.getTime() + 45 * 60000);
    const f = (d: Date) => format(d, "yyyyMMdd'T'HHmmss");
    const ics = `BEGIN:VCALENDAR\nVERSION:2.0\nBEGIN:VEVENT\nDTSTART:${f(s)}\nDTEND:${f(e)}\nSUMMARY:Turno ${spec?.name} (a confirmar) - Consultorios Integrales Urquiza\nEND:VEVENT\nEND:VCALENDAR`;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([ics], { type: 'text/calendar' }));
    a.download = 'turno-urquiza.ics'; a.click();
  };

  const renderField = (f: FormField) => {
    const id = `f-${f.id}`;
    const bad = !!errs[f.id];
    const label = <label htmlFor={id}>{f.label}{!f.required && <span className="opt"> (opcional)</span>}</label>;
    let input: React.ReactNode;
    if (f.type === 'textarea') input = <textarea id={id} className={`in ${bad ? 'bad' : ''}`} placeholder={f.placeholder} value={form[f.id] || ''} onChange={e => setF(f.id, e.target.value)} />;
    else if (f.type === 'select' || f.type === 'radio') {
      input = (
        <div className="seg" role="radiogroup" aria-label={f.label}>
          {f.options?.map(o => <button type="button" key={o} role="radio" aria-checked={form[f.id] === o} onClick={() => setF(f.id, o)}>{o}</button>)}
        </div>
      );
    } else input = <input id={id} className={`in ${bad ? 'bad' : ''}`} type={f.type === 'number' ? 'number' : f.type === 'checkbox' ? 'text' : f.type} inputMode={f.type === 'number' ? 'numeric' : undefined} placeholder={f.placeholder} value={form[f.id] || ''} onChange={e => setF(f.id, e.target.value)} />;
    return <div className="field" key={f.id}>{label}{input}{bad && <p className="err" role="alert">{errs[f.id]}</p>}</div>;
  };

  if (loadErr) return <V2><div className="wrap bk"><div className="empty">No pudimos cargar esta especialidad. <button className="btn2 btn2-ghost btn2-sm" onClick={() => nav('/')}>Volver</button></div></div></V2>;
  if (!spec) return <V2><div className="wrap bk"><div className="skel" style={{ height: 320 }} /></div></V2>;

  if (sent) return (
    <V2>
      <main className="wrap bk"><div className="done">
        <div className="tick"><Check size={36} strokeWidth={2.5} /></div>
        <h1>¡Listo, {form.name.split(' ')[0]}! Recibimos tu solicitud</h1>
        <p className="sub">{requestMode ? <>Nos pondremos en contacto con vos a <b>{form.email}</b> en menos de 24 horas hábiles para coordinar el día y horario.</> : <>Reservamos este horario para vos. Todavía no está confirmado: lo revisamos y te avisamos a <b>{form.email}</b> en menos de 24 horas hábiles. Te enviamos un email con el resumen.</>}</p>
        <div className="panel recap">
          <h3>{spec.name}</h3>
          <p style={{ marginTop: 6, fontWeight: 600 }}>{when}</p>
          <p className="hint" style={{ margin: '4px 0 0' }}>{assignedPro || proName}</p>
        </div>
        <ul className="timeline panel">
          <li className="ok"><div className="dot"><Check size={14} /></div><b>Solicitud enviada</b><span>Ya la tenemos</span></li>
          <li><div className="dot">2</div><b>{requestMode ? 'Coordinamos el horario' : 'Revisamos disponibilidad'}</b><span>En menos de 24 horas hábiles</span></li>
          <li><div className="dot">3</div><b>Te confirmamos por email</b><span>Con el profesional asignado y cómo llegar</span></li>
        </ul>
        <div className="btns">
          {!requestMode && accessToken && <Link to={`/turno/${accessToken}`} className="btn2 btn2-solid">Ver o cancelar mi reserva</Link>}
          {!requestMode && <button className="btn2 btn2-ghost" onClick={downloadIcs}><Download size={18} /> Agregar al calendario</button>}
          <button className="btn2 btn2-ghost" onClick={() => nav('/')}>Volver al inicio</button>
        </div>
      </div></main>
    </V2>
  );

  const canNext = step === 0 ? !!(date && time) : true;

  return (
    <V2>
      
      <main className="wrap bk">
        <div className="bk-top">
          <button className="bk-back" onClick={() => { if (step === 0) nav('/'); else { if (step === 1) setRequestMode(false); setStep(step - 1); } }}><ArrowLeft size={18} /> {step === 0 ? 'Especialidades' : 'Volver'}</button>
        </div>
        <div className="bk-layout">
          <section>
            <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
              <div className="avatar" style={{ width: 52, height: 52, borderRadius: 14 }}><SpecialtyIcon name={spec.icon} size={26} /></div>
              <div><h1 className="bk-title">Turno de {spec.name}</h1><p className="bk-sub">Menos de 2 minutos. No necesitás crear una cuenta.</p></div>
            </div>

            <ol className="stepper" aria-label="Progreso" style={{ padding: 0 }}>
              {STEPS.map((l, i) => (
                <li key={l} className={i < step ? 'done' : i === step ? 'cur' : ''} aria-current={i === step ? 'step' : undefined}>
                  <div className="bar"><i /></div><div className="lbl">{i + 1}<span className="t">. {l}</span></div>
                </li>
              ))}
            </ol>

            {slotTakenMsg && step === 0 && <div className="panel" role="alert" style={{ borderColor: 'var(--o-500)', background: 'var(--o-100)', marginBottom: 16 }}><b>{slotTakenMsg}</b></div>}

            {step === 0 && slotRows !== null && slotRows.length === 0 && (
              <div className="panel"><h3>Por ahora no hay horarios online</h3>
                {proId ? <p className="hint">Este profesional no tiene horarios disponibles en los próximos días. Probá con "Cualquiera".</p> : (<>
                  <p className="hint">Dejanos tus datos y nos ponemos en contacto para coordinar día y horario. Respondemos en menos de 24 horas hábiles.</p>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <button className="btn2 btn2-cta" onClick={() => { setRequestMode(true); setStep(1); }}>Pedir turno sin horario <ArrowRight size={18} /></button>
                    <a className="btn2 btn2-ghost" href={contactLink.href} {...(contactLink.external ? { target: '_blank', rel: 'noreferrer' } : {})}>Escribir por {contactLink.label}</a>
                  </div></>)}
              </div>
            )}

            {step === 0 && !(slotRows !== null && slotRows.length === 0 && !proId) && (<>
              {pros.length > 0 && (
                <div className="panel">
                  <h3>¿Con quién? <span style={{ fontFamily: "Plus Jakarta Sans", fontSize: 13, fontWeight: 500, color: "var(--muted)" }}>(opcional)</span></h3><p className="hint">Si no tenés preferencia, te asignamos el primero disponible.</p>
                  <div className="pro-row" role="radiogroup" aria-label="Profesional">
                    <button type="button" role="radio" aria-checked={proId === ANY} className="pro-chip" onClick={() => { setProId(ANY); setTime(''); }}>
                      <span className="avatar">★</span>Cualquiera
                    </button>
                    {pros.map(p => (
                      <button type="button" role="radio" aria-checked={proId === p.id} className="pro-chip" key={p.id} onClick={() => { setProId(p.id); setTime(''); }}>
                        <span className="avatar">{initials(p.name)}</span>{p.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="panel">
                <h3>Elegí día y horario</h3>
                <div className="week-nav">
                  <button className="icon-btn" aria-label="Semana anterior" disabled={weekStart <= today} onClick={() => setWeekStart(addDays(weekStart, -7))}><ChevronLeft size={20} /></button>
                  <span className="mo">{format(days[0], 'MMMM yyyy', { locale: es })}</span>
                  <button className="icon-btn" aria-label="Semana siguiente" disabled={weekStart >= addDays(today, 28)} onClick={() => setWeekStart(addDays(weekStart, 7))}><ChevronRight size={20} /></button>
                </div>
                <div className="week" role="radiogroup" aria-label="Día">
                  {days.map(d => (
                    <button type="button" key={d.toISOString()} role="radio" aria-checked={!!date && isSameDay(d, date)} disabled={!isOpen(d)} className="day" onClick={() => { setDate(d); setTime(''); setSlotTakenMsg(''); }}>
                      <span className="w">{format(d, 'EEE', { locale: es })}</span><span className="d">{format(d, 'd')}</span><span className="m">{format(d, 'MMM', { locale: es })}</span>
                    </button>
                  ))}
                </div>
                <div style={{ marginTop: 18 }}>
                  {!date && <div className="empty">Elegí un día para ver los horarios disponibles</div>}
                  {date && slots.length === 0 && <div className="empty">No quedan horarios este día. Probá con otro.</div>}
                  {morning.length > 0 && <div className="slot-group"><div className="gh"><Sun size={15} /> Mañana</div><div className="slots" role="radiogroup" aria-label="Horario de la mañana">{morning.map(s => <button type="button" role="radio" aria-checked={time === s} className="slot" key={s} onClick={() => setTime(s)}>{s}</button>)}</div></div>}
                  {afternoon.length > 0 && <div className="slot-group"><div className="gh"><Sunset size={15} /> Tarde</div><div className="slots" role="radiogroup" aria-label="Horario de la tarde">{afternoon.map(s => <button type="button" role="radio" aria-checked={time === s} className="slot" key={s} onClick={() => setTime(s)}>{s}</button>)}</div></div>}
                </div>
              </div>
            </>)}

            {step === 1 && (
              <div className="panel">
                <h3>Tus datos</h3><p className="hint">Los usamos solo para confirmar tu turno.</p>
                <div className="field"><label htmlFor="n">Nombre y apellido</label><input id="n" autoComplete="name" className={`in ${errs.name ? 'bad' : ''}`} value={form.name} onChange={e => setF('name', e.target.value)} />{errs.name && <p className="err" role="alert">{errs.name}</p>}</div>
                <div className="two">
                  <div className="field"><label htmlFor="e">Email</label><input id="e" type="email" autoComplete="email" className={`in ${errs.email ? 'bad' : ''}`} value={form.email} onChange={e => setF('email', e.target.value)} />{errs.email && <p className="err" role="alert">{errs.email}</p>}</div>
                  <div className="field"><label htmlFor="p">WhatsApp / teléfono <span className="opt">(opcional)</span></label><input id="p" type="tel" inputMode="tel" autoComplete="tel" placeholder="11 1234 5678" className={`in ${errs.phone ? 'bad' : ''}`} value={form.phone} onChange={e => setF('phone', e.target.value)} />{errs.phone && <p className="err" role="alert">{errs.phone}</p>}</div>
                </div>
                {spec.form_config.length > 0 && <hr style={{ border: 0, borderTop: '1px solid var(--line)', margin: '8px 0 20px' }} />}
                {spec.form_config.map(renderField)}
              </div>
            )}

            {step === 2 && (
              <div className="panel">
                <h3>Revisá y confirmá</h3><p className="hint">Si algo no está bien, volvé y lo cambiás.</p>
                <dl className="sum" style={{ position: 'static', border: 0, boxShadow: 'none', padding: 0 }}>
                  <dl>
                    <div><dt>Especialidad</dt><dd>{spec.name}</dd></div>
                    <div><dt>Cuándo</dt><dd>{when}</dd></div>
                    <div><dt>Profesional</dt><dd>{proName}</dd></div>
                    <div><dt>Paciente</dt><dd>{form.name} · {form.email}</dd></div>
                  </dl>
                </dl>
                <div className="sum" style={{ position: 'static', border: 0, boxShadow: 'none', padding: 0 }}><div className="note"><Info size={18} style={{ flexShrink: 0 }} /><span>Esto es una solicitud. Te confirmamos el turno por email en menos de 24 horas hábiles.</span></div></div>
                {submitErr && <p className="err" role="alert" style={{ marginTop: 14 }}>{submitErr}</p>}
              </div>
            )}
          </section>

          <aside className="bk-aside">
            <div className="sum">
              <h3>Tu turno</h3>
              <dl>
                <div><dt>Especialidad</dt><dd>{spec.name}</dd></div>
                <div><dt>Profesional</dt><dd>{proName}</dd></div>
                <div><dt>Cuándo</dt><dd className={when ? '' : 'pending'}>{when || 'Elegí día y horario'}</dd></div>
              </dl>
              <div className="note"><Info size={18} style={{ flexShrink: 0 }} /><span>Te confirmamos por email en menos de 24 horas hábiles.</span></div>
            </div>
          </aside>
        </div>
      </main>

      {!(step === 0 && slotRows !== null && slotRows.length === 0 && !proId) && <div className="bk-bar"><div className="in-bar">
        <div className="what"><b>{when || spec.name}</b><span>{when ? proName : 'Elegí día y horario'}</span></div>
        {step < 2
          ? <button className="btn2 btn2-cta" disabled={!canNext} onClick={next}>Continuar <ArrowRight size={18} /></button>
          : <button className="btn2 btn2-cta" disabled={busy} onClick={submit}>{busy ? 'Enviando…' : 'Confirmar solicitud'}</button>}
      </div></div>}
    </V2>
  );
};

export default BookingV2;
